import React, { useEffect, useRef, useState } from 'react';
import './LogsDateRangePicker.css';

const pad2 = (n: number) => String(n).padStart(2, '0');

function toIsoDateLocal(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseIsoLocal(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return null;
  const y = +m[1];
  const mo = +m[2] - 1;
  const day = +m[3];
  const d = new Date(y, mo, day);
  if (d.getFullYear() !== y || d.getMonth() !== mo || d.getDate() !== day) return null;
  return d;
}

function formatRu(ymd: string): string {
  const d = parseIsoLocal(ymd);
  if (!d) return '';
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Понедельник = 0 … воскресенье = 6 */
function weekdayMon0(d: Date): number {
  const w = d.getDay();
  return w === 0 ? 6 : w - 1;
}

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];

function daysGrid(year: number, monthIndex: number): (number | null)[] {
  const first = new Date(year, monthIndex, 1);
  const lead = weekdayMon0(first);
  const dim = new Date(year, monthIndex + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  return cells;
}

function inRange(iso: string, from: string, to: string): boolean {
  return iso >= from && iso <= to;
}

export interface LogsDateRangePickerProps {
  dateFrom: string;
  dateTo: string;
  onChange: (from: string, to: string) => void;
}

const LogsDateRangePicker: React.FC<LogsDateRangePickerProps> = ({ dateFrom, dateTo, onChange }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const base = (dateFrom && parseIsoLocal(dateFrom)) || (dateTo && parseIsoLocal(dateTo)) || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  /** Первая точка в текущей сессии выбора (второй клик фиксирует диапазон) */
  const [firstPick, setFirstPick] = useState<string | null>(null);
  const [hoverPick, setHoverPick] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setFirstPick(null);
        setHoverPick(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setFirstPick(null);
        setHoverPick(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const base = (dateFrom && parseIsoLocal(dateFrom)) || (dateTo && parseIsoLocal(dateTo)) || new Date();
    setView(new Date(base.getFullYear(), base.getMonth(), 1));
  }, [open, dateFrom, dateTo]);

  const y = view.getFullYear();
  const m = view.getMonth();
  const grid = daysGrid(y, m);

  const displayLeft = dateFrom ? formatRu(dateFrom) : '—';
  const displayRight = dateTo ? formatRu(dateTo) : '—';
  const showAll = !dateFrom && !dateTo;

  const onDayClick = (day: number) => {
    const iso = toIsoDateLocal(new Date(y, m, day));

    if (firstPick === null) {
      setFirstPick(iso);
      setHoverPick(null);
      onChange(iso, iso);
      return;
    }

    let a = firstPick;
    let b = iso;
    if (a > b) [a, b] = [b, a];
    onChange(a, b);
    setFirstPick(null);
    setHoverPick(null);
    setOpen(false);
  };

  const onDayEnter = (day: number) => {
    if (firstPick === null) return;
    setHoverPick(toIsoDateLocal(new Date(y, m, day)));
  };

  const previewFrom = firstPick && hoverPick ? (firstPick <= hoverPick ? firstPick : hoverPick) : null;
  const previewTo = firstPick && hoverPick ? (firstPick <= hoverPick ? hoverPick : firstPick) : null;

  const isInSelectedRange = (iso: string): boolean => {
    if (firstPick && !hoverPick) return iso === firstPick;
    if (previewFrom && previewTo) return inRange(iso, previewFrom, previewTo);
    if (dateFrom && dateTo) return inRange(iso, dateFrom, dateTo);
    if (dateFrom && dateTo === '' && iso === dateFrom) return true;
    return false;
  };

  const isRangeEnd = (iso: string): boolean => {
    if (previewFrom && previewTo) return iso === previewFrom || iso === previewTo;
    if (firstPick && !hoverPick) return iso === firstPick;
    if (dateFrom && dateTo) return iso === dateFrom || iso === dateTo;
    return false;
  };

  const shiftMonth = (delta: number) => {
    setView(new Date(y, m + delta, 1));
  };

  const clearRange = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('', '');
    setFirstPick(null);
    setHoverPick(null);
  };

  return (
    <div className="logs-daterange" ref={rootRef}>
      <button
        type="button"
        className="logs-daterange__trigger"
        onClick={() => {
          setOpen((v) => !v);
          setFirstPick(null);
          setHoverPick(null);
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="logs-daterange__value">
          {showAll ? (
            <span className="logs-daterange__all">За весь период</span>
          ) : (
            <>
              <span className="logs-daterange__part">{displayLeft}</span>
              <span className="logs-daterange__sep" aria-hidden>
                —
              </span>
              <span className="logs-daterange__part">{displayRight}</span>
            </>
          )}
        </span>
        <span className="logs-daterange__icon" aria-hidden>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M7 2v3M17 2v3M4 9h16M6 6h12a2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2z"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </button>

      {!showAll && (
        <button type="button" className="logs-daterange__clear" onClick={clearRange} title="Сбросить период">
          ×
        </button>
      )}

      {open && (
        <div className="logs-daterange__popover" role="dialog" aria-label="Выбор периода дат">
          <div className="logs-daterange__hint">
            {firstPick === null ? 'Выберите дату или диапазон' : 'Выберите вторую дату'}
          </div>
          <div className="logs-daterange__nav">
            <button type="button" className="logs-daterange__nav-btn" onClick={() => shiftMonth(-1)} aria-label="Предыдущий месяц">
              ‹
            </button>
            <span className="logs-daterange__month">
              {MONTHS[m]} {y}
            </span>
            <button type="button" className="logs-daterange__nav-btn" onClick={() => shiftMonth(1)} aria-label="Следующий месяц">
              ›
            </button>
          </div>
          <div className="logs-daterange__weekdays">
            {WEEKDAYS.map((w) => (
              <div key={w} className="logs-daterange__wd">
                {w}
              </div>
            ))}
          </div>
          <div className="logs-daterange__grid" onMouseLeave={() => setHoverPick(null)}>
            {grid.map((day, idx) =>
              day === null ? (
                <div key={`e-${idx}`} className="logs-daterange__cell logs-daterange__cell--empty" />
              ) : (
                (() => {
                  const iso = toIsoDateLocal(new Date(y, m, day));
                  const inSel = isInSelectedRange(iso);
                  const end = isRangeEnd(iso);
                  return (
                    <button
                      key={iso}
                      type="button"
                      className={
                        'logs-daterange__cell' +
                        (inSel ? ' logs-daterange__cell--in-range' : '') +
                        (end ? ' logs-daterange__cell--edge' : '')
                      }
                      onClick={() => onDayClick(day)}
                      onMouseEnter={() => onDayEnter(day)}
                    >
                      {day}
                    </button>
                  );
                })()
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LogsDateRangePicker;
